import Heading from 'components/Heading';
import React from 'react';
import { RateSettingsTaxClassificationInputs } from './RateSettingsTaxClassificationInputs';
import { RateSettingsBlendedInputs } from './RateSettingsBlendedInputs';
import { useFormContext } from 'react-hook-form';
import { useResourceContext } from 'react-admin';
import { Stack } from '@mui/material';
import { LOCATION_LEVEL_RATES } from '../constants/tax-classification';

interface SettingsInputProps {
  inheritedSettings: any;
  source: string;
  showTitle?: boolean;
}

interface TaxClassificationComponentProps extends SettingsInputProps {
  taxClassification: 'ten99' | 'w2.local' | 'w2.travel';
}

const DefaultTaxClassificationComponent = ({
  source,
  inheritedSettings,
  taxClassification,
}: TaxClassificationComponentProps) => {
  const { watch } = useFormContext();
  const resource = useResourceContext();
  const useBlendedRateSelectValue = watch(`${source}.${taxClassification}.use_blended_rate`);

  const isLocationLevelRate = LOCATION_LEVEL_RATES.includes(resource);
  const isStateLevelPositionRateSetting =
    resource === 'StatePositionRateSetting' || resource === 'StateLocationTypePositionRateSetting';
  const isW2TravelSettings = taxClassification === 'w2.travel';

  const shouldShowRateSection = (showCondition: boolean): boolean => {
    if (isLocationLevelRate) {
      if (useBlendedRateSelectValue === null) {
        return false;
      }

      return showCondition;
    }

    return true;
  };

  const shouldShowNonBlendedRates =
    shouldShowRateSection(useBlendedRateSelectValue === false) &&
    !isStateLevelPositionRateSetting &&
    !isW2TravelSettings;
  const shouldShowBlendedRates =
    shouldShowRateSection(useBlendedRateSelectValue === true) &&
    !isStateLevelPositionRateSetting &&
    !isW2TravelSettings;

  const commonProps = {
    source,
    inheritedSettings,
    taxClassification,
  };

  return (
    <>
      <RateSettingsTaxClassificationInputs
        {...commonProps}
        showTitle={!isStateLevelPositionRateSetting}
      />
      <Stack paddingLeft={2} width={'100%'} alignItems="flex-start">
        {shouldShowNonBlendedRates && (
          <RateSettingsBlendedInputs {...commonProps} type="8-hour-rate" />
        )}

        {shouldShowBlendedRates && (
          <RateSettingsBlendedInputs {...commonProps} type="blended-rate" />
        )}
      </Stack>
    </>
  );
};

export const Ten99Form = ({ inheritedSettings, source, showTitle = true }: SettingsInputProps) => {
  return (
    <>
      {showTitle && <Heading>1099</Heading>}
      <DefaultTaxClassificationComponent
        source={source}
        inheritedSettings={inheritedSettings}
        taxClassification="ten99"
      />
    </>
  );
};

export const W2LocalForm = ({
  inheritedSettings,
  source,
  showTitle = true,
}: SettingsInputProps) => {
  return (
    <>
      {showTitle && <Heading>W2 Local</Heading>}
      <DefaultTaxClassificationComponent
        source={source}
        inheritedSettings={inheritedSettings}
        taxClassification="w2.local"
      />
    </>
  );
};

export const W2TravelForm = ({
  inheritedSettings,
  source,
  showTitle = true,
}: SettingsInputProps) => {
  return (
    <>
      {showTitle && <Heading>W2 Travel</Heading>}
      <DefaultTaxClassificationComponent
        source={source}
        inheritedSettings={inheritedSettings}
        taxClassification="w2.travel"
      />
    </>
  );
};

const W2And1099SubSettingsForm = ({
  source,
  inheritedSettings,
}: {
  source: string;
  inheritedSettings: any;
}) => {
  return (
    <>
      <Ten99Form inheritedSettings={inheritedSettings} source={source} />
      <W2LocalForm inheritedSettings={inheritedSettings} source={source} />
      <W2TravelForm inheritedSettings={inheritedSettings} source={source} />
    </>
  );
};

export default W2And1099SubSettingsForm;
